/**
 * Presentation ViewModels — UI-specific formatting
 * Independent of application layer types (receives plain objects)
 */

import { getCdnBaseUrl } from '@/core/utils/apiUtils';
import type { NewHomeContentWidth } from '../../domain/models/NewHomePageData';

function withCdnBase(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) return trimmed;
  if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith('//')) return trimmed;
  if (!trimmed.startsWith('/')) return trimmed;
  const base = getCdnBaseUrl();
  return `${base}${trimmed.replace(/^\/+/, '')}`;
}

function withCdnBaseOptional(url: string | undefined): string | undefined {
  if (url == null) return undefined;
  return withCdnBase(url);
}

function withCdnBaseList(urls: string[] | undefined): string[] | undefined {
  if (!urls?.length) return urls;
  return urls.map((url) => withCdnBase(url));
}

export interface SectionItemPresentationVm {
  position: number;
  /** Optional analytics / CMS label (distinct from tile title copy). */
  label?: string;
  title: string;
  titleDesktop?: string;
  imageUrl: string;
  dImageUrl?: string;
  imageUrlDesktop?: string;
  videoUrl?: string;
  posterUrl?: string;
  showVideo?: boolean;
  showSlider?: boolean;
  sliderImageUrls?: string[];
  dSliderImageUrls?: string[];
  sliderOrientation?: 'horizontal' | 'vertical';
  sliderAutoPlay?: boolean;
  sliderInterval?: number;
  link: string;
  linkDesktop?: string;
  showInMobile?: boolean;
  showInDesktop?: boolean;
  videoMobileOnly?: boolean;
  showSliderOnDesktop?: boolean;
  gridArea: string;
  categoryName?: string;
  collection?: string;
  btn?: string;
  lazyload?: string;
  shouldRefresh?: boolean;
  productIds?: string[];
  overlayTextColor?: string;
  overlayBgColor?: string;
  bgColor?: string;
}

export interface SectionTitlePresentationVm {
  text: string;
  color: string;
  /** From `md` up; uses CSS variable — see SectionTitle */
  desktopColor?: string;
  align: string;
  /** CSS length; from JSON `titleStyle.fontSizeMobile`, default `24px` */
  fontSizeMobile: string;
  /** CSS length; from JSON `titleStyle.fontSizeDesktop`, default `48px` */
  fontSizeDesktop: string;
  fontWeight: string;
  fontFamily: string;
  fontStyle?: string;
}

export interface HeroPresentationVm {
  type: 'hero';
  isGradient: boolean;
  isVideo: boolean;
  /** Hero media wrapper height (CSS length, e.g. `70vh`) */
  height?: string;
  /** Hero media wrapper max-height */
  maxHeight?: string;
  bgUrl: string;
  posterUrl: string;
  fwImageUrl: string;
  fwImageBgUrl?: string;
  blankImageUrl: string;
  fwImagePosition?: string;
  fwImageWidth?: string;
  fwImageWidthDesktop?: string;
  link: string;
  autoplay?: string;
  playsinline?: string;
  loop?: string;
  muted?: string;
  bgColor: string;
  /** Color that starts the bottom-fade gradient blending into white */
  gradientColor?: string;
}

export interface GridPresentationVm {
  type: 'grid';
  title?: SectionTitlePresentationVm;
  gridTemplate: string;
  dGridTemplate: string;
  gap: string;
  bentoGap: string;
  bentoRowHeight: string;
  bentoPadding: string;
  /** Optional md+ overrides for bento spacing (mobile keeps bento*) */
  bentoGapMd?: string;
  bentoRowHeightMd?: string;
  bentoPaddingMd?: string;
  itemShape: string;
  itemAspectRatio: string;
  showItemTitle: boolean;
  itemTitlePosition: string;
  overlayBottomDesktopOnly: boolean;
  blankImageUrl: string;
  bgColor: string;
  bgGradient?: string;
  textColor: string;
  splitRows?: {
    enabled: boolean;
    firstRowCountMobile?: number;
    firstRowCountDesktop?: number;
    secondRowScrollable?: boolean;
    secondRowLoop?: boolean;
    secondRowAutoScroll?: boolean;
    secondRowMarqueeDurationSeconds?: number;
    secondRowAutoScrollDelayMs?: number;
  };
  items: SectionItemPresentationVm[];
}

export interface CarouselPresentationVm {
  type: 'carousel';
  title?: SectionTitlePresentationVm;
  slideInterval: number;
  gap: string;
  itemShape: string;
  itemAspectRatio: string;
  showItemTitle: boolean;
  blankImageUrl: string;
  bgColor: string;
  textColor: string;
  items: SectionItemPresentationVm[];
}

export interface ParallaxCarouselPresentationVm {
  type: 'parallaxCarousel';
  title?: SectionTitlePresentationVm;
  slideInterval: number;
  activeBasis: string | number;
  inactiveBasis: string | number;
  nextBasis?: string | number;
  activeBasisSm?: string | number;
  inactiveBasisSm?: string | number;
  nextBasisSm?: string | number;
  activeBasisLg?: string | number;
  inactiveBasisLg?: string | number;
  nextBasisLg?: string | number;
  snapOnClick: boolean;
  parallaxFactor: number;
  parallaxClamp: number;
  /** When false, hide parallax arrows on viewports below `md` */
  showParallaxArrowsOnMobile: boolean;
  blankImageUrl: string;
  bgColor: string;
  textColor: string;
  items: SectionItemPresentationVm[];
}

export interface ReelsPresentationVm {
  type: 'reels';
  title?: SectionTitlePresentationVm;
  slideInterval: number;
  blankImageUrl: string;
  bgColor: string;
  textColor: string;
  /** How many tiles to show in the md+ editorial row (default 3) */
  desktopVisibleCount: number;
  /**
   * Portion of one extra slide kept visible on the right (scroll hint), e.g. 0.5 with count 3 → ~3.5 cards in view.
   * Default 0.45 when omitted.
   */
  desktopPeekFraction: number;
  /** Fixed width per reel card in CSS pixels (`layout.itemWidthPx` in JSON; default 330). */
  itemWidthPx: number;
  items: SectionItemPresentationVm[];
}

export interface RecommendedCategoryFilterPresentationVm {
  label: string;
  tag: string;
  imageUrl: string;
}

export interface RecommendedPresentationVm {
  type: 'recommended';
  title?: SectionTitlePresentationVm;
  browseTags: string;
  gap: string;
  bgColor: string;
  textColor: string;
  /** Configured chips; when set, used first so filters show without menu API. */
  categoryFilters?: RecommendedCategoryFilterPresentationVm[];
}

export interface NewHomeSectionPresentationVm {
  priority: number;
  key: string;
  eventLabel: string;
  showInMobile: boolean;
  showInDesktop: boolean;
  /** From JSON `layout.contentWidth`; `container` = max-width column; `gutter` = `md`+ 5% horizontal padding */
  contentWidth?: NewHomeContentWidth;
  view:
    | HeroPresentationVm
    | GridPresentationVm
    | CarouselPresentationVm
    | ParallaxCarouselPresentationVm
    | ReelsPresentationVm
    | RecommendedPresentationVm;
}

/** `md`+ art: JSON `dImageUrl`, then legacy `imageUrlDesktop`, else `imageUrl`. */
export function resolveItemImageUrl(item: SectionItemPresentationVm, useDesktopArt: boolean): string {
  if (useDesktopArt) {
    const fromD = item.dImageUrl?.trim();
    if (fromD) return fromD;
    const legacy = item.imageUrlDesktop?.trim();
    if (legacy) return legacy;
  }
  return item.imageUrl;
}

/** `md`+ slider: `dSliderImageUrls` when non-empty, else `sliderImageUrls`. */
export function resolveItemSliderImageUrls(item: SectionItemPresentationVm, useDesktopArt: boolean): string[] {
  if (useDesktopArt && item.dSliderImageUrls && item.dSliderImageUrls.length > 0) {
    return item.dSliderImageUrls;
  }
  return item.sliderImageUrls ?? [];
}

export function resolveItemTileTitle(item: SectionItemPresentationVm, isDesktop: boolean): string {
  if (isDesktop && item.titleDesktop?.trim()) return item.titleDesktop.trim();
  return item.title;
}

export function resolveItemLink(item: SectionItemPresentationVm, isDesktop: boolean): string {
  if (isDesktop && item.linkDesktop?.trim()) return item.linkDesktop.trim();
  return item.link;
}

/**
 * Conversion from app-level plain objects to presentation VMs
 * Applies UI-specific defaults and formatting
 */

function formatTitle(
  titleArr: string[] | undefined,
  showTitle: boolean | undefined,
  titleStyle?: {
    color?: string;
    desktopColor?: string;
    align?: string;
    fontSize?: string;
    fontSizeMobile?: string;
    fontSizeDesktop?: string;
    fontWeight?: string;
    fontFamily?: string;
    fontStyle?: string;
  }
): SectionTitlePresentationVm | undefined {
  if (!showTitle || !titleArr || titleArr.length === 0) return undefined;
  const mobile = titleStyle?.fontSizeMobile?.trim() || '24px';
  const desktop = titleStyle?.fontSizeDesktop?.trim() || '48px';
  return {
    text: titleArr.join(' '),
    color: titleStyle?.color ?? '#1a1a1a',
    desktopColor: titleStyle?.desktopColor,
    align: titleStyle?.align ?? 'center',
    fontSizeMobile: mobile,
    fontSizeDesktop: desktop,
    fontWeight: titleStyle?.fontWeight ?? 'semibold',
    fontFamily: titleStyle?.fontFamily ?? 'serif',
    fontStyle: titleStyle?.fontStyle,
  };
}

function toItemPresentationVm(
  item: {
    position: number;
    label?: string;
    title: string;
    titleDesktop?: string;
    imageUrl: string;
    dImageUrl?: string;
    imageUrlDesktop?: string;
    videoUrl?: string;
    showVideo?: boolean;
    showSlider?: boolean;
    sliderImageUrls?: string[];
    dSliderImageUrls?: string[];
    sliderOrientation?: 'horizontal' | 'vertical';
    sliderAutoPlay?: boolean;
    sliderInterval?: number;
    link: string;
    linkDesktop?: string;
    showInMobile?: boolean;
    showInDesktop?: boolean;
    videoMobileOnly?: boolean;
    showSliderOnDesktop?: boolean;
    categoryName?: string;
    collection?: string;
    btn?: string;
    lazyload?: string;
    shouldRefresh?: boolean;
    productIds?: string[];
    overlayTextColor?: string;
    overlayBgColor?: string;
    bgColor?: string;
  }
): SectionItemPresentationVm {
  const labelTrim =
    typeof item.label === 'string' && item.label.trim() !== '' ? item.label.trim() : undefined;
  return {
    ...item,
    label: labelTrim,
    imageUrl: withCdnBase(item.imageUrl),
    dImageUrl: withCdnBaseOptional(item.dImageUrl),
    imageUrlDesktop: withCdnBaseOptional(item.imageUrlDesktop),
    sliderImageUrls: withCdnBaseList(item.sliderImageUrls),
    dSliderImageUrls: withCdnBaseList(item.dSliderImageUrls),
    gridArea: `item${item.position}`,
  };
}

/** Gap token → Tailwind spacing value */
function resolveGap(gap?: string): string {
  switch (gap) {
    case 'xs': return '0.25rem';
    case 'sm': return '0.5rem';
    case 'md': return '0.75rem';
    case 'lg': return '1rem';
    case 'xl': return '1.5rem';
    default: return '0.5rem';
  }
}

export function toNewHomeSectionPresentationVm(
  section: {
    priority: number;
    key: string;
    eventLabel: string;
    showInMobile: boolean;
    showInDesktop: boolean;
    value: Record<string, unknown>;
  }
): NewHomeSectionPresentationVm {
  const v = section.value;
  const layout = (v.layout ?? {}) as Record<string, unknown>;
  const style = (v.style ?? {}) as Record<string, unknown>;
  const titleStyle = v.titleStyle as {
    color?: string;
    desktopColor?: string;
    align?: string;
    fontSize?: string;
    fontSizeMobile?: string;
    fontSizeDesktop?: string;
    fontWeight?: string;
    fontFamily?: string;
    fontStyle?: string;
  } | undefined;
  const title = v.title as string[] | undefined;
  const showTitle = v.showTitle as boolean | undefined;

  const rawContentWidth = layout.contentWidth;
  const contentWidth: NewHomeContentWidth | undefined =
    rawContentWidth === 'container' || rawContentWidth === 'fluid' || rawContentWidth === 'gutter'
      ? rawContentWidth
      : undefined;

  let view: NewHomeSectionPresentationVm['view'];

  if (section.key === 'heroVideo') {
    view = {
      type: 'hero',
      isGradient: (v.isGradient as boolean) ?? false,
      isVideo: (v.isVideo as boolean) ?? false,
      height: v.height as string | undefined,
      maxHeight: v.maxHeight as string | undefined,
      bgUrl: (v.bgUrl as string) ?? '',
      posterUrl: withCdnBase((v.posterUrl as string) ?? ''),
      fwImageUrl: withCdnBase((v.fwImageUrl as string) ?? ''),
      fwImageBgUrl: withCdnBaseOptional(v.fwImageBgUrl as string | undefined),
      blankImageUrl: withCdnBase((v.blankImageUrl as string) ?? ''),
      fwImagePosition: v.fwImagePosition as string | undefined,
      fwImageWidth: v.fwImageWidth as string | undefined,
      fwImageWidthDesktop: v.fwImageWidthDesktop as string | undefined,
      link: (v.link as string) ?? '',
      autoplay: v.autoplay as string | undefined,
      playsinline: v.playsinline as string | undefined,
      loop: v.loop as string | undefined,
      muted: v.muted as string | undefined,
      bgColor: (style.bgColor as string) ?? '#000000',
      gradientColor: style.gradientColor as string | undefined,
    };
  } else if (section.key === 'recommended') {
    const rawCf = v.categoryFilters;
    let categoryFilters: RecommendedCategoryFilterPresentationVm[] | undefined;
    if (Array.isArray(rawCf)) {
      categoryFilters = rawCf
        .map((entry) => {
          const o = entry as Record<string, unknown>;
          return {
            label: String(o.label ?? '').trim(),
            tag: String(o.tag ?? '').trim(),
            imageUrl: withCdnBase(String(o.imageUrl ?? '').trim()),
          };
        })
        .filter((f) => f.label.length > 0);
      if (categoryFilters.length === 0) categoryFilters = undefined;
    }
    view = {
      type: 'recommended',
      title: formatTitle(title, showTitle, titleStyle),
      browseTags: (v.browseTags as string) ?? '',
      gap: resolveGap(layout.gap as string | undefined),
      bgColor: (style.bgColor as string) ?? '#ffffff',
      textColor: (style.textColor as string) ?? '#1a1a1a',
      categoryFilters,
    };
  } else if ((layout.template as string) === 'reels') {
    const items = (v.items as Array<Record<string, unknown>>) ?? [];
    const rawDesktopCount = v.desktopVisibleCount as number | undefined;
    const desktopVisibleCount =
      typeof rawDesktopCount === 'number' &&
      Number.isFinite(rawDesktopCount) &&
      rawDesktopCount > 0
        ? Math.floor(rawDesktopCount)
        : 3;
    const rawItemWidthPx = layout.itemWidthPx as number | undefined;
    const itemWidthPx =
      typeof rawItemWidthPx === 'number' && Number.isFinite(rawItemWidthPx) && rawItemWidthPx > 0
        ? Math.round(rawItemWidthPx)
        : 330;
    const rawPeek =
      (v.desktopPeekFraction as number | undefined) ??
      (layout.desktopPeekFraction as number | undefined);
    const desktopPeekFraction =
      typeof rawPeek === 'number' &&
      Number.isFinite(rawPeek) &&
      rawPeek >= 0.05 &&
      rawPeek <= 1.25
        ? rawPeek
        : 0.45;
    view = {
      type: 'reels',
      title: formatTitle(title, showTitle, titleStyle),
      slideInterval: (v.slideInterval as number) ?? 5000,
      blankImageUrl: withCdnBase((v.blankImageUrl as string) ?? ''),
      bgColor: (style.bgColor as string) ?? '#ffffff',
      textColor: (style.textColor as string) ?? '#1a1a1a',
      desktopVisibleCount,
      desktopPeekFraction,
      itemWidthPx,
      items: items.map((item) => toItemPresentationVm(item as unknown as SectionItemPresentationVm)),
    };
  } else if ((layout.template as string) === 'carousel') {
    const items = (v.items as Array<Record<string, unknown>>) ?? [];
    view = {
      type: 'carousel',
      title: formatTitle(title, showTitle, titleStyle),
      slideInterval: (v.slideInterval as number) ?? 3000,
      gap: resolveGap(layout.gap as string | undefined),
      itemShape: (layout.itemShape as string) ?? 'rounded',
      itemAspectRatio: (layout.itemAspectRatio as string) ?? '3:4',
      showItemTitle: (layout.showItemTitle as boolean) ?? false,
      blankImageUrl: withCdnBase((v.blankImageUrl as string) ?? ''),
      bgColor: (style.bgColor as string) ?? '#ffffff',
      textColor: (style.textColor as string) ?? '#1a1a1a',
      items: items.map((item) => toItemPresentationVm(item as unknown as SectionItemPresentationVm)),
    };
  } else if ((layout.template as string) === 'parallax-carousel') {
    const items = (v.items as Array<Record<string, unknown>>) ?? [];
    view = {
      type: 'parallaxCarousel',
      title: formatTitle(title, showTitle, titleStyle),
      slideInterval: (v.slideInterval as number) ?? 3500,
      activeBasis: (v.activeBasis as string | number) ?? '82%',
      inactiveBasis: (v.inactiveBasis as string | number) ?? '16%',
      nextBasis: v.nextBasis as string | number | undefined,
      activeBasisSm: v.activeBasisSm as string | number | undefined,
      inactiveBasisSm: v.inactiveBasisSm as string | number | undefined,
      nextBasisSm: v.nextBasisSm as string | number | undefined,
      activeBasisLg: v.activeBasisLg as string | number | undefined,
      inactiveBasisLg: v.inactiveBasisLg as string | number | undefined,
      nextBasisLg: v.nextBasisLg as string | number | undefined,
      snapOnClick: (v.snapOnClick as boolean) ?? true,
      parallaxFactor: (v.parallaxFactor as number) ?? 0.22,
      parallaxClamp: (v.parallaxClamp as number) ?? 18,
      showParallaxArrowsOnMobile: (v.showParallaxArrowsOnMobile as boolean) !== false,
      blankImageUrl: withCdnBase((v.blankImageUrl as string) ?? ''),
      bgColor: (style.bgColor as string) ?? '#ffffff',
      textColor: (style.textColor as string) ?? '#1a1a1a',
      items: items.map((item) => toItemPresentationVm(item as unknown as SectionItemPresentationVm)),
    };
  } else {
    // Grid sections: categoryGrid, promoBanner, handpicked
    const items = (v.items as Array<Record<string, unknown>>) ?? [];
    view = {
      type: 'grid',
      title: formatTitle(title, showTitle, titleStyle),
      gridTemplate: (v.gridLayout as string) ?? '',
      dGridTemplate: (v.dGridLayout as string) ?? '',
      gap: resolveGap(layout.gap as string | undefined),
      bentoGap: (layout.bentoGap as string) ?? '5px',
      bentoRowHeight: (layout.bentoRowHeight as string) ?? '26vw',
      bentoPadding: (layout.bentoPadding as string) ?? '0px',
      bentoGapMd: layout.dBentoGap as string | undefined,
      bentoRowHeightMd: layout.dBentoRowHeight as string | undefined,
      bentoPaddingMd: layout.dBentoPadding as string | undefined,
      itemShape: (layout.itemShape as string) ?? 'rounded',
      itemAspectRatio: (layout.itemAspectRatio as string) ?? '1:1',
      showItemTitle: (layout.showItemTitle as boolean) ?? false,
      itemTitlePosition: (layout.itemTitlePosition as string) ?? 'below',
      overlayBottomDesktopOnly: (layout.overlayBottomDesktopOnly as boolean) ?? false,
      blankImageUrl: withCdnBase((v.blankImageUrl as string) ?? ''),
      bgColor: (style.bgColor as string) ?? '#ffffff',
      bgGradient: style.bgGradient as string | undefined,
      textColor: (style.textColor as string) ?? '#1a1a1a',
      splitRows: (() => {
        const raw = layout.splitRows as Record<string, unknown> | undefined;
        if (!raw) return undefined;
        return {
          enabled: (raw.enabled as boolean) ?? false,
          firstRowCountMobile: raw.firstRowCountMobile as number | undefined,
          firstRowCountDesktop: raw.firstRowCountDesktop as number | undefined,
          secondRowScrollable: raw.secondRowScrollable as boolean | undefined,
          secondRowLoop: raw.secondRowLoop as boolean | undefined,
          secondRowAutoScroll: raw.secondRowAutoScroll as boolean | undefined,
          secondRowMarqueeDurationSeconds: raw.secondRowMarqueeDurationSeconds as number | undefined,
          secondRowAutoScrollDelayMs: raw.secondRowAutoScrollDelayMs as number | undefined,
        };
      })(),
      items: items.map((item) => toItemPresentationVm(item as unknown as SectionItemPresentationVm)),
    };
  }

  return {
    priority: section.priority,
    key: section.key,
    eventLabel: section.eventLabel,
    showInMobile: section.showInMobile,
    showInDesktop: section.showInDesktop,
    contentWidth,
    view,
  };
}

export function toNewHomeSectionsPresentationVm(
  sections: Array<{
    priority: number;
    key: string;
    eventLabel: string;
    showInMobile: boolean;
    showInDesktop: boolean;
    value: Record<string, unknown>;
  }>
): NewHomeSectionPresentationVm[] {
  return sections.map(toNewHomeSectionPresentationVm);
}
